'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React, { lazy, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { ProductColors } from '../product-colors';
import { ProductBadge } from '../detail/product-badge';
import { ProductAttributes } from '../product-attributes';
import { Paragraph } from '../../ui/paragraph';
import { Button } from '../../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import Link from '@eliadmin/core/src/components/link';

export type ProductCardProps = {
  deferImages?: boolean;
  product: Product;
  variant?: 'horizontal' | 'vertical';
};

const prefetchedProductUrls = new Set<string>();

function VerticalProductCard({
  product,
  variant = 'vertical',
  deferImages = false,
}: ProductCardProps) {
  const templateSettings = useGlobalStore((s) => s.templateSettings);
  // Only attribute filters affect this card's variant image. Sorting, price and
  // manufacturer changes must not rerender every unchanged card.
  const filter = useCategoryStore(
    (state) => state.filter,
    (previous, next) => previous.attributes === next.attributes,
  );

  const { t } = useTranslations();

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, []);
  const isWholesaler = useCustomerStore((s) => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });

  const router = useRouter();
  const hoverPrefetchTimer = React.useRef<number | null>(null);

  const prefetchProductUrl = React.useCallback(() => {
    if (!productUrl || prefetchedProductUrls.has(productUrl)) return;

    prefetchedProductUrls.add(productUrl);
    try {
      router.prefetch(productUrl);
    } catch {
      // Prefetch is opportunistic and must never block navigation.
    }
  }, [productUrl, router]);

  const cancelProductPrefetch = React.useCallback(() => {
    if (hoverPrefetchTimer.current === null) return;

    window.clearTimeout(hoverPrefetchTimer.current);
    hoverPrefetchTimer.current = null;
  }, []);

  const scheduleProductPrefetch = React.useCallback(() => {
    cancelProductPrefetch();
    hoverPrefetchTimer.current = window.setTimeout(() => {
      hoverPrefetchTimer.current = null;
      prefetchProductUrl();
    }, 250);
  }, [cancelProductPrefetch, prefetchProductUrl]);

  const navigateToProduct = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (
        event.defaultPrevented ||
        (event.target as Element).closest('a,button,input,select,textarea,[role="button"]')
      )
        return;
      cancelProductPrefetch();
      router.push(productUrl);
    },
    [cancelProductPrefetch, productUrl, router],
  );

  React.useEffect(() => cancelProductPrefetch, [cancelProductPrefetch]);

  const isDiscounted = priceDisplay.showPreviousPrice;

  if (variant === 'vertical') {
    /**
     * Redesigned 1:1 from the Figma "Product cards" component: white card, 320px image plate,
     * 24px body block, and a permanent walnut-outline "Pozrieť produkt" CTA in place of the old
     * hover-revealed add-to-cart bar. The favourites button stays on hover — it is invisible in
     * the resting state the design specifies, so it costs nothing visually while keeping the
     * feature the card already had.
     */
    return (
      <div
        onMouseEnter={scheduleProductPrefetch}
        onMouseLeave={cancelProductPrefetch}
        data-search-navigation
        onClick={navigateToProduct}
        className={cn(
          'group',
          'col-span-1 flex w-full cursor-pointer flex-col items-start justify-start gap-3 rounded-[4px] bg-white transition-all ease-in-out sm:gap-4',
        )}
      >
        <div className="relative h-[240px] w-full overflow-hidden rounded-[4px] bg-white sm:h-[280px] lg:h-[320px]">
          <div className="absolute left-3 top-3 z-30 flex items-start justify-start gap-2 pr-14 lg:left-4 lg:top-4">
            {product.tags?.map((tag) => (
              <ProductBadge
                key={tag.name}
                text={tag.name}
                background={tag.color ?? '#F1E4DE'}
                color={getContrastYIQ(tag.color ?? '#F1E4DE')}
              />
            ))}
            {isDiscounted && (
              <ProductBadge
                text={`-${priceDisplay.discountLabel}%`}
                background="#F1DEDE"
                color="#9B2929"
              />
            )}
          </div>
          {/* Hover-reveal only where hover exists: on touch screens the invisible button still
              caught taps on the image corner and toggled a favourite. */}
          <div className="absolute right-2 top-2 z-10 transition-all ease-in-out focus-within:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100">
            <AddToFavoritesButton
              buttonSize={'icon-scaling'}
              slug={product.slug}
              buttonVariant={'white'}
              isFavorite={product.is_favorite ?? false}
              buttonClassName={cn('ea-product-card__favorites-button')}
              productId={product.id}
            />
          </div>

          <Link
            href={productUrl}
            prefetch={false}
            prefetchOnHover={false}
            className="absolute inset-0"
          >
            <Image
              deferUntilNearViewport={deferImages}
              src={image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(image, product?.name)}
              width={0}
              height={0}
              className="absolute inset-0 z-0 h-full w-full rounded-[4px] bg-white object-contain object-center p-2 transition-transform duration-500 group-hover:scale-105 md:p-4"
              sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, (max-width: 1280px) 25vw, 20vw"
            />
          </Link>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-4 p-3 pt-0 @container sm:gap-5 lg:gap-6 lg:p-6 lg:pt-0">
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <h3 className="line-clamp-2 min-h-[2.6em] w-full text-[15px] font-medium leading-[1.3] text-[#29251F] sm:text-[16px]">
              <Link href={productUrl} prefetch={false} prefetchOnHover={false}>
                {product.name}
              </Link>
            </h3>
            <ProductAttributes attributes={product.attributes} />
            <ProductPrice product={product} className={{ priceAfterDiscount: 'text-[#29251F]' }} />
            {priceDisplay.priceOnRequest && (
              <Paragraph size="base" className="font-bold text-primary">
                {t('text2278')}
              </Paragraph>
            )}
            <ProductColors product={product} />
          </div>
          <Button
            asChild
            variant="brand-bordered"
            size="card"
            // One line on phones: in the 2-column grid the card body is only ~115–150px wide, so the
            // label scales with it (bold Montserrat "Zobrazit produkt" ≈ 8.8em + 20px padding) —
            // measured to fit from 320px viewports up, 14px max. `truncate` is the backstop so it
            // can never spill out of the card.
            className="ea-product-card__detail-button min-h-12 w-fit max-w-full whitespace-nowrap rounded-[8px] px-2.5 text-[length:clamp(10px,calc(11cqi_-_3px),14px)] sm:px-8 sm:text-base"
          >
            <Link href={productUrl} prefetch={false} prefetchOnHover={false}>
              <span className="truncate">{t('text1254')}</span>
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

const HorizontalProductCard = lazy(() =>
  import('./horizontal-product-card').then((module) => ({ default: module.ProductCard })),
);

export const ProductCard = React.memo(function ProductCard(props: ProductCardProps) {
  if (props.variant === 'horizontal') {
    return (
      <Suspense fallback={<ProductCardSkeleton amount={1} variant="horizontal" />}>
        <HorizontalProductCard {...props} />
      </Suspense>
    );
  }
  // This presentation only links to the detail. Cart and variant providers belong to the horizontal action card.
  return <VerticalProductCard {...props} />;
});

export function ProductCardSkeleton({
  amount,
  variant = 'vertical',
}: {
  amount: number;
  variant?: 'horizontal' | 'vertical';
}) {
  return (
    <>
      {Array.from({ length: amount }).map((_, i) => (
        <div
          key={i}
          className={cn(
            'animate-pulse',
            'col-span-1 w-full',
            variant === 'vertical'
              ? 'flex flex-col items-start justify-start gap-3 sm:gap-4'
              : 'flex items-center justify-start gap-3 pr-2 sm:gap-4 md:pr-3 lg:pr-4',
          )}
        >
          <div
            className={cn(
              'aspect-square w-full rounded-[4px] bg-neutral-100',
              variant === 'horizontal' && 'max-w-[160px] sm:max-w-[170px] lg:max-w-[190px]',
            )}
          />
          <div className={cn('h-[100px] w-full rounded-[4px] bg-neutral-100 sm:h-[113px]')} />
        </div>
      ))}
    </>
  );
}
